import React, { useContext, useEffect, useState, useRef } from 'react';
import { withTranslation } from "react-i18next";
import { toast } from 'react-toastify';
import { Button, Form } from 'react-bootstrap';
import ContentBlockService from '../../core/service/ContentBlockService';
import HTMLEditor from '../Editor/HTMLEditor';
import { BsFileEarmarkText, BsGlobe } from "react-icons/bs";
import { AiOutlineClose } from 'react-icons/ai';
import { LS_UI_ELEMENTS_CONFIG_KEY, LS_AUTHORITY_KEY } from '../../core/constants/common';
import { LoaderContext, RegulationContext } from '../../App';
import esg_ai from '../../images/esg_ai.png';
import './AIContentExtract.scss';
import { Spinner } from 'react-bootstrap';
import Utils from '../../core/utilities/Utils';

const AIContentExtract = ({ t, onHideWidget, transferContent }) => {

	const lsData = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY));
	const { setLoaderDisplay } = useContext(LoaderContext);
	const [searchType, setSearchType] = useState('file');
	const [url, setUrl] = useState('');
	const [fileUploader, setFileUploader] = useState('');
	const [searchResult, setSearchResult] = useState('');
	const companyName = lsData.profile.company.legalName;
	const companyId = lsData.profile.company.id;
	const editorRefExtract = useRef(null);
	const [whatToLook, setWhatToLook] = useState('');
	const fileInputRef = useRef();
	const uiElementsConfig = JSON.parse(localStorage.getItem(LS_UI_ELEMENTS_CONFIG_KEY)) ?? null;
	const [isLoading, setIsLoading] = useState(false);

	const resetAdvancedSearch = () => {
		setSearchType('file');
		setUrl('');
		setFileUploader('');
		setSearchResult('');
		setWhatToLook('');
		editorRefExtract.current.setContent('');
		fileInputRef.current.value = null;
	}

	const handleFileSelect = (event) => {
		const fileUploaded = event.target.files[0];
		if (Utils.contentExtractionFileExceedsMaxSize(fileUploaded)) {
			fileInputRef.current.value = null;
			toast.warning('Upload files less than 5MB', {
				position: toast.POSITION.TOP_RIGHT,
				autoClose: 3000
			});
		} else {
			setFileUploader(fileUploaded);			
		}
	};

	const dataFromFile = () => {
		if (fileUploader && whatToLook) {
			const formData = new FormData();
			formData.append("file", fileUploader);
			formData.append("company_id", companyId);
			formData.append("company_name", companyName);
			formData.append("tag", whatToLook ? whatToLook : null);
			setIsLoading(true);
			ContentBlockService.contentFromFile(formData)
				.then(res => {
					setIsLoading(false);
					setSearchResult(replaceTableStyle(res.data.content));
				})
				.catch((error) => {
					setIsLoading(false);
				});
		} else {
			toast.warning('Please select a file and provide what to look for', {
				position: toast.POSITION.TOP_RIGHT,
				autoClose: 3000
			});
		}
	}

	const replaceTableStyle = (data) => {
		let htmlString = data.replaceAll('<table border=\"1\">', '<table className="table table-bordered">')
		return htmlString
	}

	const dataFromUrl = () => {
		if (url && isValidURL(url) && whatToLook) {
			let passData = {
				url: url,
				company_id: companyId,
				company_name: companyName,
				tag: whatToLook ? whatToLook : null
			}
			setIsLoading(true);
			ContentBlockService.contentFromUrl(passData)
				.then(res => {
					setIsLoading(false);
					setSearchResult(res.data.content);
				})
				.catch((error) => {
					setIsLoading(false);
				});
		} else {
			toast.warning('Please provide a valid url and provide what to look for', {
				position: toast.POSITION.TOP_RIGHT,
				autoClose: 3000
			});
		}
	}

	const extractData = () => {
		if (searchType === 'file') {
			dataFromFile()
		} else {
			dataFromUrl();
		}
	}

	const isValidURL = (string) => {
		let res = string.match(/(http(s)?:\/\/.)[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/g);
		return (res !== null)
	};

	const transferTextToEditor = () => {
		if (editorRefExtract?.current?.getContent()) {
			transferContent(editorRefExtract?.current?.getContent());
		} else {
			toast.warning("No contents to transfer", {
				position: toast.POSITION.TOP_RIGHT,
				autoClose: 2000
			});
		}
	}

	const handleAIContentExtractClose = () => {
		onHideWidget();
	}

	const renderSpinnerLoader = () => {
		return (
			<div className='text-center'>
				<Spinner animation="grow" variant="warning" />
				<Spinner animation="grow" variant="warning" />
				<Spinner animation="grow" variant="warning" />
			</div>
		);
	};

	return (
		<div id='contentExtractWidget' className='1'>
			<div className='widget-header text-white'>
				<span className='widget-header_inner'><img src={esg_ai} width={15} height={15} /> {t('ExtractWithESGAI.label')}</span>
				<span 
					className="cursor" 
					onClick={() => { resetAdvancedSearch(); handleAIContentExtractClose() }}>
					<AiOutlineClose />
				</span>
			</div>
			<div className='widget-body px-2 pt-2'>
				<div className='Advanced_search_other'>
					<div className='p-1 pt-0'>
						{/* <label className='mb-1'>Search from</label> */}
						<div className='pb-1'>
							<h6> Upload a file or input a URL, and ESGAI® will automatically search and extract the data</h6>
						</div>
						<div className='Search_from_01'>
							<Form.Check
								inline
								className='select_Advancedbox'
								label={<><BsFileEarmarkText className='me-1' /> File</>}
								name="assessType"
								type="radio"
								id="inline-radio-2"
								value="file"
								checked={searchType === 'file' ? "checked" : ""}
								onChange={(e) => { setSearchType(e.target.value) }}
							/>
							<Form.Check
								className='select_Advancedbox'
								inline
								label={<><BsGlobe className='me-1' /> URL</>}
								name="assessType"
								type="radio"
								id="inline-radio-1"
								value="url"
								checked={searchType === 'url' ? "checked" : ""}
								onChange={(e) => { setSearchType(e.target.value) }}
							/>
						</div>
					</div>

					<div className='p-1'>
						<label className='mb-1'>{t("WhatToLook.label")} <span className="text-danger">*</span></label>
						<div className='d-flex'>
							<input className='form-control ' value={whatToLook}
								onChange={(e) => setWhatToLook(e.target.value)} placeholder={t("WhatToLookPlaceholder.label")} />
						</div>
					</div>

					{searchType === 'file' && (
						<div className='p-1'>
							<label className='mb-1'>Supported formats: .pdf, .txt, .docx, .xlsx, .pptx less than 5MB <span className="text-danger">*</span></label>
							<div className='d-flex'>
								<input
									className='form-control '
									type="file"
									id="imageUpload"
									accept=".pdf, .txt, .docx, .xlsx, .pptx"
									onChange={(e) => handleFileSelect(e)}
									onClick={(e) => {
										e.target.value = null;
									}}
									ref={fileInputRef}
								/>
								<Button variant="primary" className="sbmBtn ms-2 " onClick={() => { extractData() }}>
									{t("Extract.label")}
								</Button>
							</div>
						</div>
					)}

					{searchType === 'url' && (
						<div className='p-1'>
							<label className='mb-1'>{t("UrlSearch.label")} <span className="text-danger">*</span></label>
							<div className='d-flex'>
								<input className='form-control ' value={url}
									onChange={(e) => setUrl(e.target.value)} placeholder='eg: https://google.com' />
								<Button variant="primary" className="sbmBtn ms-2" onClick={() => { extractData() }}>
									{t("Extract.label")}
								</Button>
							</div>
						</div>
					)}
					{
						isLoading && renderSpinnerLoader()
					}
					<div className='p-1'>
						<label className='mb-1'>{t("TranscribedText.label")}</label>
						<div id="extract">
							<HTMLEditor editorRef={editorRefExtract} initialValue={searchResult} selectorVal={'#extract'} />
						</div>
					</div>

					<div className='text-center'>
						<Button variant="secondary" className='px-3' onClick={() => resetAdvancedSearch()} disabled={isLoading}>
							{t("Reset.label")}
						</Button>
						<Button variant="primary" className="btn btn-ESGAi mx-2" onClick={() => transferTextToEditor()} disabled={isLoading}>
							{t("TransferData.label")}
						</Button>
					</div>

					<div className='text-center AITextGenWidge_footerBox'>
						{
							uiElementsConfig?.texts?.copilotHelp
								? uiElementsConfig?.texts?.copilotHelp
								:
								<>
									<div className='text-muted Info_textGen'>
										<sapn>
											{t('AITextGenWidgetFootNote1.label')}
										</sapn>
										<div className="Privacy_termsbox">
											<ul>
												<li>
													<a className="" target="_blank" href={uiElementsConfig?.texts?.termsAndConditions ?? "https://ecodrisil.com/terms-of-use"}>
														Terms & Conditions
													</a>
												</li>
												<li>Feedback @ <a href="mailto:copilot@ecodrisil.com">copilot@ecodrisil.com</a>
												</li>
											</ul>
										</div>
									</div>


								</>
						}
					</div>
				</div>
			</div>
		</div>

	)
}

export default withTranslation()(AIContentExtract);
